import type { FuseInternalPortalUserInfo } from "@shared/fuse-internal-api";

import type { FusePermissions } from "./permissions";

import { createContext, useContext, useEffect } from "react";

import { setContext } from "@sentry/react";

import { FullScreenLoader } from "../../components/src/full-screen-loader";
import { authQueryClient } from "../../fuse-internal-api/src/client";

function redirectToLogin() {
	const params = new URLSearchParams(window.location.search);
	const invitation = params.get("invitation");
	const organization = params.get("organization");
	const redirectUrl = new URL(`${import.meta.env.VITE_FUSE_API_BASE_URL}/auth/login`);
	const appBaseUrl = `${window.location.origin}/`;

	if (invitation) {
		redirectUrl.searchParams.set("invitation", invitation);
	}
	if (organization) {
		redirectUrl.searchParams.set("organization", organization);
	}
	redirectUrl.searchParams.set("redirect_url", appBaseUrl);
	window.location.href = redirectUrl.toString();
}

export const AuthContext = createContext<Omit<FuseInternalPortalUserInfo, "permissions">>({
	email: undefined,
	email_verified: undefined,
	intercom_user_jwt: "",
	name: undefined,
	picture: undefined,
	scopes: [] as FusePermissions[],
	sub: "",
});

export function useAuth() {
	return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const {
		data: user,
		error,
		isFetched,
		isLoading,
	} = authQueryClient.useAuthUserInfo(
		{},
		{
			refetchOnWindowFocus: false,
			retry: (failureCount, error) => {
				if (error.message.includes("401")) {
					return false;
				}
				return failureCount <= 1;
			},
		},
	);
	useEffect(() => {
		if (user) {
			// Set the user context for Sentry
			setContext("user", {
				email: user.email,
				name: user.name,
			});
		}
	}, [user]);

	if (isLoading || !isFetched) {
		return <FullScreenLoader />;
	}

	if (error) {
		redirectToLogin();
		return <FullScreenLoader />;
	}

	if (!user) {
		redirectToLogin();
		return <FullScreenLoader />;
	}

	return <AuthContext.Provider value={user}>{children}</AuthContext.Provider>;
}
